import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  FileText,
  Calendar,
  RotateCcw,
  Sparkles,
  FlaskConical,
  Dumbbell,
  Moon,
  Zap,
  Target,
  Clock,
  PackageOpen,
  User,
} from 'lucide-react';
import { submitQuiz } from '@/lib/api';
import SectionHeader from '@/components/SectionHeader';

interface QuizOption {
  value: string;
  label: string;
  description?: string;
}

interface QuizQuestion {
  id: string;
  question: string;
  subtitle?: string;
  icon: React.ReactNode;
  options: QuizOption[];
}

const QUESTIONS: QuizQuestion[] = [
  {
    id: 'primary-goal',
    question: 'What is your primary goal?',
    subtitle: 'Pick the outcome that matters most to you right now.',
    icon: <Target size={20} />,
    options: [
      { value: 'longevity', label: 'Longevity & vitality', description: 'Cellular health, energy, and healthy ageing' },
      { value: 'body-composition', label: 'Body composition', description: 'Lean mass, fat loss, and metabolic support' },
      { value: 'recovery', label: 'Recovery & repair', description: 'Tendon, joint, gut, and wound healing' },
      { value: 'cognition', label: 'Cognition & focus', description: 'Mental clarity, memory, and mood resilience' },
      { value: 'energy', label: 'Energy & performance', description: 'Stamina, mitochondrial function, and drive' },
    ],
  },
  {
    id: 'experience',
    question: 'How experienced are you with peptides?',
    subtitle: 'This helps us calibrate dose guidance and education depth.',
    icon: <FlaskConical size={20} />,
    options: [
      { value: 'new', label: 'Completely new', description: 'I am researching my first peptide' },
      { value: 'tried', label: 'Tried before', description: 'I have used one or two compounds' },
      { value: 'regular', label: 'Regular user', description: 'I run structured peptide protocols' },
    ],
  },
  {
    id: 'age',
    question: 'What is your age range?',
    subtitle: 'Age influences recovery capacity and hormone axis considerations.',
    icon: <User size={20} />,
    options: [
      { value: '18-29', label: '18 – 29' },
      { value: '30-39', label: '30 – 39' },
      { value: '40-49', label: '40 – 49' },
      { value: '50+', label: '50 +' },
    ],
  },
  {
    id: 'support-area',
    question: 'Which area needs the most support?',
    subtitle: 'Choose the system you would like to prioritise in your plan.',
    icon: <Sparkles size={20} />,
    options: [
      { value: 'sleep', label: 'Sleep & recovery', description: 'Deeper rest and faster bounce-back' },
      { value: 'focus', label: 'Focus & stress', description: 'Mental stamina and calm under pressure' },
      { value: 'metabolism', label: 'Metabolism', description: 'Appetite, energy expenditure, and body comp' },
      { value: 'skin-joints', label: 'Skin & joints', description: 'Collagen support, healing, and mobility' },
    ],
  },
  {
    id: 'training',
    question: 'How many days per week do you train?',
    subtitle: 'Training load affects recovery peptide dosing and timing.',
    icon: <Dumbbell size={20} />,
    options: [
      { value: '0-1', label: '0 – 1 days' },
      { value: '2-3', label: '2 – 3 days' },
      { value: '4-5', label: '4 – 5 days' },
      { value: '6+', label: '6+ days' },
    ],
  },
  {
    id: 'preference',
    question: 'Single peptide or a stack?',
    subtitle: 'Stacks can be more synergistic; singles are simpler to track.',
    icon: <PackageOpen size={20} />,
    options: [
      { value: 'single', label: 'Single peptide', description: 'Start with one focused compound' },
      { value: 'stack', label: 'A stack', description: 'Combine complementary peptides' },
      { value: 'either', label: 'Open to either', description: 'Recommend the best fit for my goals' },
    ],
  },
  {
    id: 'timing',
    question: 'When would you like to start?',
    subtitle: 'This affects how we structure your onboarding and shipping.',
    icon: <Clock size={20} />,
    options: [
      { value: 'asap', label: 'As soon as possible' },
      { value: 'month', label: 'Within a month' },
      { value: 'researching', label: 'Just researching' },
    ],
  },
  {
    id: 'admin-time',
    question: 'Do you have a preferred dosing time?',
    subtitle: 'Some peptides are morning-only; others work best in the evening.',
    icon: <Moon size={20} />,
    options: [
      { value: 'morning', label: 'Morning' },
      { value: 'evening', label: 'Evening' },
      { value: 'pre-workout', label: 'Pre-workout' },
      { value: 'no-preference', label: 'No preference' },
    ],
  },
];

const containerVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -12, transition: { duration: 0.25 } },
};

const optionVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.35, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function QuizPage() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: submitQuiz,
  });

  const current = QUESTIONS[step];
  const progress = ((step + (mutation.isSuccess ? 1 : 0)) / QUESTIONS.length) * 100;
  const selected = answers[current.id];

  function handleSelect(value: string) {
    setAnswers((prev) => ({ ...prev, [current.id]: value }));
  }

  function handleNext() {
    if (step < QUESTIONS.length - 1) {
      setStep((s) => s + 1);
    } else {
      mutation.mutate(answers);
    }
  }

  function handleBack() {
    if (step > 0) setStep((s) => s - 1);
  }

  function handleRestart() {
    setStep(0);
    setAnswers({});
    mutation.reset();
  }

  return (
    <div className="min-h-full bg-rp-paper">
      <section className="py-10 md:py-20">
        <div className="rp-container">
          <div className="mx-auto max-w-3xl">
            <SectionHeader
              eyebrow="Free · personalized plan"
              title={
                <>
                  A free plan, <em className="not-italic font-medium text-rp-forest">built around you.</em>
                </>
              }
              description="Answer eight quick questions and we build a personalized peptide report from your goals, reviewed with you in a free 15-minute specialist consultation. No commitment."
              align="center"
            />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="overflow-hidden rounded-3xl border border-rp-line bg-rp-bone shadow-panel"
            >
              {/* Progress */}
              <div className="border-b border-rp-line bg-rp-paper px-6 py-4 md:px-8 md:py-5">
                <div className="flex items-center justify-between">
                  <span className="rp-mono-mini text-rp-moss">
                    Question {Math.min(step + 1, QUESTIONS.length)} of {QUESTIONS.length}
                  </span>
                  <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-rp-moss">
                    {Math.round(progress)}%
                  </span>
                </div>
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-rp-bone-2">
                  <motion.div
                    className="h-full rounded-full bg-rp-forest"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
              </div>

              {/* Quiz body */}
              <div className="min-h-[420px] p-6 md:p-10">
                <AnimatePresence mode="wait">
                  {!mutation.isSuccess && !mutation.isError && (
                    <motion.div
                      key={current.id}
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="flex flex-col"
                    >
                      <div className="mb-6 flex items-center gap-3 md:mb-8">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full border border-rp-line bg-rp-paper text-rp-forest">
                          {current.icon}
                        </span>
                        <div>
                          <h2 className="text-lg font-semibold text-rp-ink md:text-xl">{current.question}</h2>
                          {current.subtitle && <p className="mt-0.5 text-sm text-rp-moss">{current.subtitle}</p>}
                        </div>
                      </div>

                      <div className="grid gap-3 md:grid-cols-2">
                        {current.options.map((option, i) => {
                          const isSelected = selected === option.value;
                          return (
                            <motion.button
                              key={option.value}
                              type="button"
                              custom={i}
                              variants={optionVariants}
                              initial="hidden"
                              animate="visible"
                              onClick={() => handleSelect(option.value)}
                              className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition-all duration-200 md:p-5 ${
                                isSelected
                                  ? 'border-rp-forest bg-rp-paper shadow-card-hover'
                                  : 'border-rp-line bg-rp-paper hover:border-rp-forest/40 hover:shadow-card-hover'
                              }`}
                            >
                              <span
                                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                                  isSelected ? 'border-rp-forest bg-rp-forest text-rp-paper' : 'border-rp-line-2'
                                }`}
                              >
                                {isSelected && <Check size={12} strokeWidth={3} />}
                              </span>
                              <div>
                                <span className="block font-semibold text-rp-ink">{option.label}</span>
                                {option.description && (
                                  <span className="mt-1 block text-xs leading-relaxed text-rp-moss">
                                    {option.description}
                                  </span>
                                )}
                              </div>
                            </motion.button>
                          );
                        })}
                      </div>

                      <div className="mt-8 flex items-center justify-between md:mt-10">
                        <button
                          type="button"
                          onClick={handleBack}
                          disabled={step === 0}
                          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-mono text-xs font-bold uppercase tracking-wider text-rp-moss transition-colors hover:bg-rp-paper disabled:pointer-events-none disabled:opacity-40"
                        >
                          <ArrowLeft size={14} />
                          Back
                        </button>
                        <button
                          type="button"
                          onClick={handleNext}
                          disabled={!selected || mutation.isPending}
                          className="rp-btn-primary disabled:pointer-events-none disabled:opacity-50"
                        >
                          {mutation.isPending ? (
                            <span className="flex items-center gap-2">
                              <span className="h-4 w-4 animate-spin rounded-full border-2 border-rp-paper/30 border-t-rp-paper" />
                              Building plan…
                            </span>
                          ) : step === QUESTIONS.length - 1 ? (
                            <>
                              Get my plan <Sparkles size={14} />
                            </>
                          ) : (
                            <>
                              Next <ArrowRight size={14} />
                            </>
                          )}
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {mutation.isError && (
                    <motion.div
                      key="error"
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="flex h-full flex-col items-center justify-center py-10 text-center"
                    >
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-rp-warn/10 text-rp-warn">
                        <Zap size={24} />
                      </div>
                      <h2 className="mt-5 text-xl font-semibold text-rp-ink">Something went wrong</h2>
                      <p className="mt-2 max-w-sm text-sm text-rp-moss">
                        We could not build your report right now. Please check your connection and try again.
                      </p>
                      <button
                        type="button"
                        onClick={handleRestart}
                        className="rp-btn-outline mt-6"
                      >
                        <RotateCcw size={14} />
                        Restart quiz
                      </button>
                    </motion.div>
                  )}

                  {mutation.isSuccess && (
                    <motion.div
                      key="result"
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="flex flex-col"
                    >
                      <div className="mb-6 flex items-center gap-3 md:mb-8">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-rp-leaf-2 text-rp-paper">
                          <Check size={20} strokeWidth={3} />
                        </span>
                        <div>
                          <h2 className="text-lg font-semibold text-rp-ink md:text-xl">Your personalized peptide report</h2>
                          <p className="mt-0.5 text-sm text-rp-moss">
                            Based on {QUESTIONS.length} answers · Reviewed free with a specialist
                          </p>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-rp-line bg-rp-paper p-5 md:p-7">
                        <div className="flex items-start gap-3">
                          <FileText className="mt-1 shrink-0 text-rp-forest" size={20} />
                          <div>
                            <h3 className="font-semibold text-rp-ink">Report summary</h3>
                            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-rp-moss">
                              {mutation.data.report}
                            </p>
                          </div>
                        </div>

                        <div className="mt-6 flex flex-col gap-3 border-t border-rp-line pt-6 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-center gap-2 text-sm text-rp-moss">
                            <Calendar size={16} />
                            <span>Free 15-minute specialist consultation</span>
                          </div>
                          <a
                            href={mutation.data.bookingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rp-btn-primary"
                          >
                            Book free review <ArrowRight size={14} />
                          </a>
                        </div>
                      </div>

                      <div className="mt-6 grid gap-3 rounded-2xl border border-rp-line bg-rp-paper p-5 md:grid-cols-3">
                        {[
                          { label: 'Personalized', desc: 'Matched to your goals' },
                          { label: 'Free', desc: 'No commitment required' },
                          { label: 'Two minutes', desc: 'Quick specialist review' },
                        ].map((tag) => (
                          <div key={tag.label} className="flex items-center gap-3">
                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-rp-bone text-rp-forest">
                              <Check size={14} strokeWidth={3} />
                            </span>
                            <div>
                              <p className="text-sm font-semibold text-rp-ink">{tag.label}</p>
                              <p className="text-xs text-rp-moss">{tag.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-8 flex justify-center">
                        <button
                          type="button"
                          onClick={handleRestart}
                          className="rp-btn-ghost"
                        >
                          <RotateCcw size={14} />
                          Retake quiz
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* Trust strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-rp-moss md:mt-10"
            >
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rp-leaf-2" />
                Free
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rp-leaf-2" />
                Two minutes
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rp-leaf-2" />
                No commitment
              </span>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
